export const DEFAULT_SCANNER_EXCHANGE = 'BINANCE';
export const DEFAULT_SCANNER_TIMEFRAME = '4H';
export const DEFAULT_SCANNER_UNIVERSE = 'top300';
export const DEFAULT_PATTERN_ENGINE_VERSION = 6;
export const DEFAULT_TRADE_ENGINE_VERSION = 1;
export const HISTORICAL_PATTERN_ENGINE_VERSIONS = [1, 3, 4, 5, 6, 7, 8, 9];
export const DEFAULT_BUCKET_ENGINE_VERSIONS = [6, 8];
export const BUCKET_TIMEFRAMES = [
  { value: '1H', label: 'H1' },
  { value: '4H', label: 'H4' },
  { value: '1D', label: 'D1' },
];
export const DEFAULT_BUCKET_TIMEFRAMES = ['4H'];
export const SCANNER_DETAIL_TIMEFRAMES = ['1H', '4H', '1D'];
export const SCANNER_PAGE_SIZE = 10;
export const NO_CLEAR_PATTERN_KEY = 'NO_CLEAR_PATTERN';

const PATTERN_ENGINE_VERSION_LABELS = {
  1: 'v1',
  3: 'v3',
  4: 'v4',
  5: 'v4.2',
  6: 'v4.3',
  7: 'v5.0',
  8: 'v5.1',
  9: 'v5.2',
};

const TRADE_ENGINE_VERSION_LABELS = {
  1: 'v1',
  3: 'v3',
  4: 'v4',
};

export function resolvePatternEngineVersionLabel(value) {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && Object.prototype.hasOwnProperty.call(PATTERN_ENGINE_VERSION_LABELS, numericValue)) {
    return PATTERN_ENGINE_VERSION_LABELS[numericValue];
  }
  return Number.isFinite(numericValue) ? `v${numericValue}` : 'v?';
}

export function resolveTradeEngineVersionLabel(value) {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && Object.prototype.hasOwnProperty.call(TRADE_ENGINE_VERSION_LABELS, numericValue)) {
    return TRADE_ENGINE_VERSION_LABELS[numericValue];
  }
  return Number.isFinite(numericValue) ? `v${numericValue}` : 'v?';
}

export function normalizeText(value, fallback = '') {
  if (typeof value !== 'string') {
    return fallback;
  }
  const normalized = value.trim();
  return normalized || fallback;
}

export function normalizeUpper(value, fallback = '') {
  return normalizeText(value, fallback).toUpperCase();
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeScoreMetric(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }
  if (numericValue >= 0 && numericValue <= 1) {
    return Math.round(numericValue * 100);
  }
  return Math.round(numericValue);
}

function normalizeV3ScoreSnapshot(snapshot) {
  if (!isPlainObject(snapshot)) {
    return null;
  }
  return {
    score: normalizeScoreMetric(snapshot.score),
    label: normalizeUpper(snapshot.label),
    components: isPlainObject(snapshot.components) ? snapshot.components : {},
  };
}

function resolvePatternLikeSource(source) {
  return isPlainObject(source?.pattern) ? source.pattern : source;
}

export function parseScannerRawPayload(rawJson) {
  const normalized = normalizeText(rawJson);
  if (!normalized) {
    return null;
  }
  try {
    const parsed = JSON.parse(normalized);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function resolvePatternEngineV2Metadata(source) {
  const rawPayload = parseScannerRawPayload(source?.rawJson);
  const diagnostics = isPlainObject(rawPayload?.diagnostics) ? rawPayload.diagnostics : {};
  const geometry = isPlainObject(diagnostics.geometry) ? diagnostics.geometry : null;
  const structure = isPlainObject(diagnostics.structure) ? diagnostics.structure : null;
  const events = isPlainObject(diagnostics.events) ? diagnostics.events : null;
  const patternFamily = isPlainObject(diagnostics.patternFamily) ? diagnostics.patternFamily : null;
  const confidenceBreakdown = isPlainObject(diagnostics.confidenceBreakdown) ? diagnostics.confidenceBreakdown : null;
  const explanation = isPlainObject(diagnostics.explanation) ? diagnostics.explanation : null;
  const patterns = Array.isArray(diagnostics.patterns)
    ? diagnostics.patterns.filter((entry) => isPlainObject(entry) && normalizeUpper(entry.name))
    : [];
  const swings = Array.isArray(diagnostics.swings)
    ? diagnostics.swings.filter((entry) => isPlainObject(entry) && normalizeText(entry.label))
    : [];
  const fakeGeometryFlags = Array.isArray(diagnostics.fakeGeometryFlags)
    ? diagnostics.fakeGeometryFlags.filter((entry) => typeof entry === 'string' && entry.trim())
    : [];
  const selectedPattern = normalizeUpper(diagnostics.selectedPattern || rawPayload?.selectedPattern);
  const structureQualityThreshold = Number.isFinite(Number(diagnostics.structureQualityThreshold))
    ? Number(diagnostics.structureQualityThreshold)
    : null;

  return {
    geometry,
    structure,
    events,
    patternFamily,
    confidenceBreakdown,
    explanation,
    patterns,
    swings,
    fakeGeometryFlags,
    selectedPattern,
    structureQualityThreshold,
  };
}

export function resolvePatternEngineV3Metadata(source) {
  const rawPayload = parseScannerRawPayload(source?.rawJson);
  const diagnostics = isPlainObject(rawPayload?.diagnostics) ? rawPayload.diagnostics : {};
  const rawV3 = isPlainObject(diagnostics?.v3) ? diagnostics.v3 : null;
  if (!rawV3) {
    return null;
  }

  const directionalBias = isPlainObject(rawV3.directionalBias)
    ? {
      code: normalizeUpper(rawV3.directionalBias.code, ''),
      bullishProbability: normalizeScoreMetric(rawV3.directionalBias.bullishProbability),
      bearishProbability: normalizeScoreMetric(rawV3.directionalBias.bearishProbability),
      dominantProbability: normalizeScoreMetric(rawV3.directionalBias.dominantProbability),
      spread: normalizeScoreMetric(rawV3.directionalBias.spread),
      structureBias: normalizeUpper(rawV3.directionalBias.structureBias, ''),
      patternDirection: normalizeUpper(rawV3.directionalBias.patternDirection, ''),
      source: normalizeUpper(rawV3.directionalBias.source, ''),
    }
    : null;
  const rendering = isPlainObject(rawV3.rendering)
    ? {
      annotationPattern: normalizeUpper(rawV3.rendering.annotationPattern, ''),
      annotationDirection: normalizeUpper(rawV3.rendering.annotationDirection, ''),
      annotationLocked: rawV3.rendering.annotationLocked === true,
      overlaySource: normalizeUpper(rawV3.rendering.overlaySource, ''),
    }
    : null;

  return {
    version: Number.isFinite(Number(rawV3.version)) ? Number(rawV3.version) : null,
    selectedPattern: normalizeUpper(rawV3.selectedPattern || rendering?.annotationPattern, ''),
    patternQuality: normalizeV3ScoreSnapshot(rawV3.patternQuality),
    breakoutQuality: normalizeV3ScoreSnapshot(rawV3.breakoutQuality),
    directionalBias,
    setupConfidence: normalizeV3ScoreSnapshot(rawV3.setupConfidence),
    rendering,
  };
}

export function resolveCanonicalBias(source) {
  const patternSource = resolvePatternLikeSource(source);
  const rawBias = isPlainObject(patternSource?.bias)
    ? patternSource.bias
    : isPlainObject(source?.bias)
      ? source.bias
      : null;
  const metadata = resolvePatternEngineV2Metadata(patternSource);
  const bullishProbability = Number.isFinite(Number(rawBias?.bullishProbability ?? patternSource?.pumpProbability))
    ? Math.round(Number(rawBias?.bullishProbability ?? patternSource?.pumpProbability))
    : null;
  const bearishProbability = Number.isFinite(Number(rawBias?.bearishProbability ?? patternSource?.dumpProbability))
    ? Math.round(Number(rawBias?.bearishProbability ?? patternSource?.dumpProbability))
    : null;
  let code = normalizeUpper(rawBias?.code || source?.summary?.bias || patternSource?.direction, 'NEUTRAL');

  if (bullishProbability !== null && bearishProbability !== null) {
    if (bullishProbability > bearishProbability) {
      code = 'BULLISH';
    } else if (bearishProbability > bullishProbability) {
      code = 'BEARISH';
    } else {
      code = 'NEUTRAL';
    }
  }

  const dominantProbability = Number.isFinite(Number(rawBias?.dominantProbability))
    ? Math.round(Number(rawBias.dominantProbability))
    : bullishProbability !== null && bearishProbability !== null
      ? Math.max(bullishProbability, bearishProbability)
      : bullishProbability ?? bearishProbability;

  return {
    code,
    bullishProbability,
    bearishProbability,
    dominantProbability,
    structureBias: normalizeUpper(rawBias?.structureBias || metadata.structure?.bias, ''),
    patternDirection: normalizeUpper(rawBias?.patternDirection || patternSource?.direction, ''),
    source: normalizeUpper(rawBias?.source, 'FALLBACK'),
  };
}

export function resolveCanonicalBreakout(source) {
  const patternSource = resolvePatternLikeSource(source);
  const rawPayload = parseScannerRawPayload(patternSource?.rawJson);
  const diagnostics = isPlainObject(rawPayload?.diagnostics) ? rawPayload.diagnostics : {};
  const validation = isPlainObject(diagnostics.validation)
    ? diagnostics.validation
    : isPlainObject(rawPayload?.validation)
      ? rawPayload.validation
      : {};
  const rawBreakout = isPlainObject(patternSource?.breakout)
    ? patternSource.breakout
    : isPlainObject(source?.breakout)
      ? source.breakout
      : null;
  const level = toFiniteNumber(
    rawBreakout?.level
      ?? patternSource?.breakoutLevel
      ?? source?.graph?.breakoutLevel
      ?? validation.breakoutLevel
      ?? validation.breakdownLevel,
  );
  const targetPrice = toFiniteNumber(
    rawBreakout?.targetPrice
      ?? patternSource?.targetPrice
      ?? source?.graph?.targetPrice
      ?? validation.targetPrice,
  );
  const stopInvalidation = toFiniteNumber(
    rawBreakout?.stopInvalidation
      ?? patternSource?.stopInvalidation
      ?? source?.graph?.stopInvalidation
      ?? validation.stopInvalidation,
  );
  const riskRewardRatio = Number.isFinite(Number(rawBreakout?.riskRewardRatio ?? patternSource?.riskRewardRatio))
    ? Number(rawBreakout?.riskRewardRatio ?? patternSource?.riskRewardRatio)
    : null;
  let direction = normalizeUpper(rawBreakout?.direction, '');

  if (!direction && level !== null && targetPrice !== null) {
    if (targetPrice > level && (stopInvalidation === null || stopInvalidation < level)) {
      direction = 'BULLISH';
    } else if (targetPrice < level && (stopInvalidation === null || stopInvalidation > level)) {
      direction = 'BEARISH';
    }
  }

  if (!direction) {
    direction = resolveCanonicalBias(source).code;
  }

  return {
    type: normalizeUpper(rawBreakout?.type, direction === 'BEARISH' ? 'BREAKDOWN' : direction === 'BULLISH' ? 'BREAKOUT' : ''),
    direction: direction || 'NEUTRAL',
    status: normalizeUpper(rawBreakout?.status || validation.breakoutState || diagnostics.events?.breakoutState, ''),
    statusLabel: normalizeText(rawBreakout?.statusLabel || validation.breakoutStateLabel || diagnostics.events?.breakoutStateLabel, ''),
    level,
    targetPrice,
    stopInvalidation,
    riskRewardRatio,
    tradeFirstValidated: typeof rawBreakout?.tradeFirstValidated === 'boolean'
      ? rawBreakout.tradeFirstValidated
      : typeof validation.tradeFirstValidated === 'boolean'
        ? validation.tradeFirstValidated
        : null,
  };
}

export function resolveCoherenceSnapshot(source) {
  const patternSource = resolvePatternLikeSource(source);
  const rawCoherence = isPlainObject(patternSource?.coherence)
    ? patternSource.coherence
    : isPlainObject(source?.coherence)
      ? source.coherence
      : null;

  if (!rawCoherence) {
    return null;
  }

  return {
    ...rawCoherence,
    overallStatus: normalizeUpper(rawCoherence.overallStatus, ''),
    issues: Array.isArray(rawCoherence.issues) ? rawCoherence.issues : [],
    summary: normalizeText(rawCoherence.summary, ''),
  };
}

export function resolveDisplayedPatternKey(source) {
  const metadata = resolvePatternEngineV2Metadata(source);
  if (metadata.selectedPattern) {
    return metadata.selectedPattern;
  }
  return normalizeUpper(source?.patternDetected || source?.detectedPattern || source?.pattern || source?.summary?.pattern);
}

export function resolveProjectionDisplayState(source) {
  return {
    shouldDisplayProjectionLevels: true,
    title: '',
    description: '',
  };
}

export function resolveGeometryTypeLabel(type) {
  const normalized = normalizeUpper(type);
  switch (normalized) {
    case 'HORIZONTAL':
      return 'Horizontale';
    case 'ASCENDING':
      return 'Ascendante';
    case 'ASCENDING_PARALLEL':
      return 'Ascendante parallèle';
    case 'DESCENDING':
      return 'Descendante';
    case 'DESCENDING_PARALLEL':
      return 'Descendante parallèle';
    case 'CONVERGING':
      return 'Convergente';
    case 'DIVERGING':
      return 'Divergente';
    case 'CURVED':
      return 'Courbe';
    default:
      return normalizeText(type, 'n/a');
  }
}

export function resolveConvictionScore(source) {
  if (Number.isFinite(source?.conviction?.score)) {
    return Math.round(source.conviction.score);
  }
  if (Number.isFinite(source?.summary?.convictionScore)) {
    return Math.round(source.summary.convictionScore);
  }
  if (Number.isFinite(source?.convictionScore)) {
    return Math.round(source.convictionScore);
  }
  if (Number.isFinite(source?.signalFusion?.convictionScore)) {
    return Math.round(source.signalFusion.convictionScore);
  }
  if (Number.isFinite(source?.multiTimeframe?.insights?.convictionScore)) {
    return Math.round(source.multiTimeframe.insights.convictionScore);
  }
  if (Number.isFinite(source?.multiTimeframeDetail?.convictionScore)) {
    return Math.round(source.multiTimeframeDetail.convictionScore);
  }
  if (Number.isFinite(source?.multiTimeframeScore)) {
    return Math.round(source.multiTimeframeScore);
  }
  if (Number.isFinite(source?.scoreGlobal)) {
    return Math.round(source.scoreGlobal);
  }
  if (Number.isFinite(source?.patternScore)) {
    return Math.round(source.patternScore);
  }
  return 0;
}

export function resolveScore(source) {
  if (Number.isFinite(source?.scoreGlobal)) {
    return Math.round(source.scoreGlobal);
  }
  if (Number.isFinite(source?.patternScore)) {
    return Math.round(source.patternScore);
  }
  return resolveConvictionScore(source);
}

export function resolveAverageScore(source) {
  if (Number.isFinite(source?.multiTimeframeScore)) {
    return Math.round(source.multiTimeframeScore);
  }
  if (Number.isFinite(source?.multiTimeframeDetail?.convictionScore)) {
    return Math.round(source.multiTimeframeDetail.convictionScore);
  }
  if (Number.isFinite(source?.multiTimeframe?.insights?.convictionScore)) {
    return Math.round(source.multiTimeframe.insights.convictionScore);
  }
  return null;
}

export function resolveConvictionLabel(score) {
  if (score >= 85) {
    return 'Grade institutionnel';
  }
  if (score >= 75) {
    return 'Setup fort';
  }
  if (score >= 60) {
    return 'Setup confirmé';
  }
  return 'Sous surveillance';
}

export function resolveRiskDescriptor(source) {
  const fakeoutRisk = Number.isFinite(source?.fakeoutRisk) ? source.fakeoutRisk : Number.isFinite(source?.conviction?.fakeoutRisk) ? source.conviction.fakeoutRisk : 50;
  const riskRewardRatio = Number.isFinite(source?.riskRewardRatio) ? source.riskRewardRatio : Number.isFinite(source?.conviction?.riskRewardRatio) ? source.conviction.riskRewardRatio : 0;
  const targetConfidence = Number.isFinite(source?.targetConfidence) ? source.targetConfidence : Number.isFinite(source?.conviction?.targetConfidence) ? source.conviction.targetConfidence : 0;

  if (fakeoutRisk <= 30 && riskRewardRatio >= 1.8 && targetConfidence >= 0.7) {
    return { label: 'Faible', tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' };
  }
  if (fakeoutRisk <= 45 && riskRewardRatio >= 1.2 && targetConfidence >= 0.55) {
    return { label: 'Modéré', tone: '#8d6c16', background: 'rgba(141,108,22,0.14)' };
  }
  return { label: 'Élevé', tone: '#aa3d33', background: 'rgba(170,61,51,0.14)' };
}

export function resolveDirectionDescriptor(direction) {
  const normalized = normalizeUpper(direction, 'NEUTRAL');
  if (normalized === 'BULLISH') {
    return { label: 'Haussier', tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' };
  }
  if (normalized === 'BEARISH') {
    return { label: 'Baissier', tone: '#aa3d33', background: 'rgba(170,61,51,0.12)' };
  }
  return { label: 'Neutre', tone: '#5f6f75', background: 'rgba(95,111,117,0.12)' };
}

export function resolveVolumeDescriptor(source) {
  const confirmationScore = Number.isFinite(source?.volumeConfirmationScore) ? source.volumeConfirmationScore : 0;
  if (source?.volumeConfirmation || confirmationScore >= 0.7) {
    return { label: 'Confirmé', tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' };
  }
  if (confirmationScore >= 0.45) {
    return { label: 'En construction', tone: '#8d6c16', background: 'rgba(141,108,22,0.14)' };
  }
  return { label: 'Fragile', tone: '#aa3d33', background: 'rgba(170,61,51,0.14)' };
}

export function resolveAlignmentScore(source) {
  if (Number.isFinite(source?.multiTimeframe?.insights?.alignmentScore)) {
    return Math.round(source.multiTimeframe.insights.alignmentScore);
  }
  if (Number.isFinite(source?.multiTimeframeDetail?.alignmentScore)) {
    return Math.round(source.multiTimeframeDetail.alignmentScore);
  }
  if (Number.isFinite(source?.multiTimeframeScore)) {
    return Math.round(source.multiTimeframeScore);
  }
  return null;
}

export function resolveAlignmentDescriptor(source) {
  const score = resolveAlignmentScore(source);
  if (!Number.isFinite(score)) {
    return { label: 'Non disponible', tone: '#5f6f75', background: 'rgba(95,111,117,0.12)' };
  }
  if (score >= 80) {
    return { label: `Aligné ${score}`, tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' };
  }
  if (score >= 60) {
    return { label: `Mixte ${score}`, tone: '#8d6c16', background: 'rgba(141,108,22,0.14)' };
  }
  return { label: `Faible ${score}`, tone: '#aa3d33', background: 'rgba(170,61,51,0.14)' };
}

export function formatConfidence(value) {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  const normalized = value > 1 ? value : value * 100;
  return `${Math.round(normalized)}%`;
}

export function formatPercent(value) {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

export function formatRatio(value) {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  return `${value.toFixed(2)}x`;
}

export function formatRiskReward(value) {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  return `1:${value.toFixed(1)}`;
}

export function formatProbability(value) {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

export function toFiniteNumber(value) {
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized.replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function resolveTradeDirectionDescriptor(direction) {
  const normalized = normalizeUpper(direction, 'WAIT');
  if (normalized === 'LONG') {
    return { label: 'LONG', tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' };
  }
  if (normalized === 'SHORT') {
    return { label: 'SHORT', tone: '#aa3d33', background: 'rgba(170,61,51,0.12)' };
  }
  return { label: 'WAIT', tone: '#5f6f75', background: 'rgba(95,111,117,0.12)' };
}

export function formatPrice(value) {
  const numericValue = toFiniteNumber(value);
  if (!Number.isFinite(numericValue)) {
    return 'n/a';
  }
  if (Math.abs(numericValue) >= 1000) {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(numericValue);
  }
  if (Math.abs(numericValue) >= 1) {
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(numericValue);
  }
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(numericValue);
}

export function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'n/a';
  }
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatHistoryDate(value) {
  if (!value) {
    return 'Date inconnue';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Date inconnue';
  }
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

export function formatRelativeTargetMove(source) {
  const breakoutLevel = source?.breakoutLevel;
  const targetPrice = source?.targetPrice;
  if (!Number.isFinite(breakoutLevel) || !Number.isFinite(targetPrice) || breakoutLevel === 0) {
    return 'n/a';
  }
  const move = ((targetPrice - breakoutLevel) / breakoutLevel) * 100;
  const prefix = move > 0 ? '+' : '';
  return `${prefix}${move.toFixed(1)}%`;
}

export function hasAdvancedSignalCoverage(source) {
  return Boolean(
    source?.signalFusionState
    || source?.signalFusion?.state
    || source?.divergenceType
    || source?.divergence?.type
    || source?.momentumState
    || source?.momentum?.state
    || source?.candlestickPattern
    || source?.candlestick?.pattern
  );
}

export function clampSummaryText(text) {
  const normalized = normalizeText(text);
  if (!normalized) {
    return 'Aucun résumé disponible.';
  }
  return normalized;
}
